<template>
  <div v-if="show" class="custom-modal-overlay" @click="handleOverlayClick">
    <div class="custom-modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="custom-modal-header">
        <h4 class="modal-title">{{ modalTitle }}</h4>
        <button type="button" class="btn-close" @click="$emit('cancel')">
          <feather-icon icon="XIcon" size="20" />
        </button>
      </div>

      <!-- Modal Body -->
      <div class="custom-modal-body">
        <div class="translation-preview-modal">
          <!-- Header with stats -->
          <div class="mb-4 p-3 bg-light rounded">
            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-1">
                  <feather-icon icon="ZapIcon" size="16" class="mr-2 text-warning" />
                  Proponowane tłumaczenia AI
                </h6>
                <p class="text-muted mb-0">{{ modalDescription }}</p>
              </div>
              <div class="col-md-6 text-right">
                <b-badge variant="primary" class="mr-2">
                  {{ Object.keys(proposedTranslations).length }} kluczy
                </b-badge>
                <b-badge variant="success"> {{ selectedCount }} wybranych </b-badge>
              </div>
            </div>
          </div>

          <!-- Selection Controls -->
          <div class="mb-3 d-flex justify-content-between align-items-center">
            <div>
              <b-button variant="outline-primary" size="sm" @click="selectAll" class="mr-2">
                <feather-icon icon="CheckSquareIcon" size="14" class="mr-1" />
                Zaznacz wszystkie
              </b-button>
              <b-button variant="outline-warning" size="sm" @click="selectOnlyMissing" class="mr-2">
                <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />
                Tylko brakujące
              </b-button>
              <b-button variant="outline-secondary" size="sm" @click="deselectAll">
                <feather-icon icon="SquareIcon" size="14" class="mr-1" />
                Odznacz wszystkie
              </b-button>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-between pt-2 mb-2 border-top">
            <div>
              <small class="text-muted">
                {{ selectedCount }} z {{ totalTranslations }} tłumaczeń zostanie zaktualizowanych
              </small>
            </div>
            <div>
              <b-button variant="success" @click="acceptSelected" :disabled="selectedCount === 0" class="mr-2">
                <feather-icon icon="CheckIcon" size="14" class="mr-1" />
                {{ acceptButtonText }}
              </b-button>
              <b-button variant="outline-secondary" @click="$emit('cancel')">
                <feather-icon icon="XIcon" size="14" class="mr-1" />
                Anuluj
              </b-button>
            </div>
          </div>

          <!-- Translations List -->
          <div class="translations-preview-list" style="max-height: 70vh; overflow-y: auto">
            <div
              v-for="(translations, key) in proposedTranslations"
              :key="key"
              class="translation-preview-item mb-2 border rounded p-2">
              <!-- Translation Key Header -->
              <div
                class="d-flex justify-content-between align-items-center mb-2 key-header-clickable"
                @click="toggleKeySelectionClick(key)">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    :checked="isKeySelected(key)"
                    @change="toggleKeySelection(key, $event)"
                    @click.stop
                    class="mr-2">
                  </b-form-checkbox>
                  <code class="text-primary translation-key-compact">{{ key }}</code>
                </div>
                <small class="text-muted">{{ getOriginalPolishText(key) }}</small>
              </div>

              <!-- Proposed Translations - Compact Layout -->
              <div class="row no-gutters">
                <div
                  v-for="language in availableLanguages"
                  :key="language.lang"
                  class="col-md-4 mb-1 pr-1"
                  v-show="filterLanguage === 'all' || filterLanguage === language.lang">
                  <div
                    v-if="language.lang !== 'pl'"
                    class="language-translation-compact border rounded p-1 clickable-card"
                    :class="{ 'border-success bg-light': isLanguageSelected(key, language.lang) }"
                    @click="toggleLanguageSelectionClick(key, language.lang)">
                    <!-- Language Header - Compact -->
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div class="d-flex align-items-center">
                        <b-form-checkbox
                          :checked="isLanguageSelected(key, language.lang)"
                          @change="toggleLanguageSelection(key, language.lang, $event)"
                          @click.stop
                          class="mr-1">
                        </b-form-checkbox>
                        <small class="language-label-compact">{{ language.value }}</small>
                      </div>
                      <b-badge
                        :variant="isLanguageSelected(key, language.lang) ? 'success' : 'secondary'"
                        class="badge-compact">
                        {{ isLanguageSelected(key, language.lang) ? '✓' : 'AI' }}
                      </b-badge>
                    </div>

                    <!-- Proposed Translation Only - Compact -->
                    <div class="proposed-translation-compact">
                      {{ translations[language.locale] }}
                    </div>

                    <!-- Current translation as tooltip or small text if different -->
                    <small v-if="getCurrentTranslation(key, language.lang)" class="text-muted d-block current-compact">
                      {{ getCurrentTranslation(key, language.lang) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TranslationPreviewModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    proposedTranslations: {
      type: Object,
      default: () => ({}),
    },
    availableLanguages: {
      type: Array,
      default: () => [],
    },
    getTranslationsForKey: {
      type: Function,
      required: true,
    },
    isSingleKey: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        console.log('TranslationPreviewModal show prop changed:', newValue);
      },
    },
    proposedTranslations: {
      immediate: true,
      handler(newValue) {
        console.log('TranslationPreviewModal proposedTranslations prop changed:', newValue);
      },
    },
  },
  data() {
    return {
      selectedTranslations: {}, // { key: { 'en-US': true, 'hu-HU': false, ... } }
      filterLanguage: 'all',
    };
  },
  computed: {
    selectedCount() {
      let count = 0;
      Object.values(this.selectedTranslations).forEach((languages) => {
        Object.values(languages).forEach((isSelected) => {
          if (isSelected) count++;
        });
      });
      return count;
    },

    totalTranslations() {
      return Object.keys(this.proposedTranslations).length * (this.availableLanguages.length - 1); // -1 for Polish
    },

    acceptButtonText() {
      if (this.isSingleKey) {
        return `Zastosuj w polach (${this.selectedCount})`;
      }
      return `Zaakceptuj wybrane (${this.selectedCount})`;
    },

    modalTitle() {
      if (this.isSingleKey) {
        return 'Podgląd tłumaczenia AI dla klucza';
      }
      return 'Podgląd tłumaczeń AI';
    },

    modalDescription() {
      if (this.isSingleKey) {
        return 'Przejrzyj tłumaczenia i zastosuj je w polach edycji';
      }
      return 'Przejrzyj i wybierz tłumaczenia, które chcesz zaakceptować';
    },
  },
  watch: {
    proposedTranslations: {
      immediate: true,
      handler(newTranslations) {
        this.initializeSelections(newTranslations);
      },
    },
  },
  methods: {
    initializeSelections(translations) {
      const selected = {};
      Object.keys(translations).forEach((key) => {
        selected[key] = {};
        this.availableLanguages.forEach((lang) => {
          if (lang.lang !== 'pl' && translations[key][lang.locale]) {
            // By default, select all proposed translations
            selected[key][lang.locale] = true;
          }
        });
      });
      this.selectedTranslations = selected;
    },

    isKeySelected(key) {
      if (!this.selectedTranslations[key]) return false;
      return Object.values(this.selectedTranslations[key]).some((isSelected) => isSelected);
    },

    isLanguageSelected(key, languageCode) {
      const locale = this.getLocaleCode(languageCode);
      return this.selectedTranslations[key]?.[locale] || false;
    },

    toggleKeySelection(key, isSelected) {
      if (!this.selectedTranslations[key]) return;

      Object.keys(this.selectedTranslations[key]).forEach((locale) => {
        this.selectedTranslations[key][locale] = isSelected;
      });

      this.$forceUpdate();
    },

    toggleLanguageSelection(key, languageCode, isSelected) {
      const locale = this.getLocaleCode(languageCode);
      if (!this.selectedTranslations[key]) {
        this.$set(this.selectedTranslations, key, {});
      }

      this.$set(this.selectedTranslations[key], locale, isSelected);
    },

    toggleKeySelectionClick(key) {
      // Toggle all languages for this key
      const currentState = this.isKeySelected(key);
      this.toggleKeySelection(key, !currentState);
    },

    toggleLanguageSelectionClick(key, languageCode) {
      // Toggle individual language selection
      const currentState = this.isLanguageSelected(key, languageCode);
      this.toggleLanguageSelection(key, languageCode, !currentState);
    },

    selectAll() {
      Object.keys(this.proposedTranslations).forEach((key) => {
        if (!this.selectedTranslations[key]) {
          this.$set(this.selectedTranslations, key, {});
        }
        this.availableLanguages.forEach((lang) => {
          if (lang.lang !== 'pl' && this.proposedTranslations[key][lang.locale]) {
            this.$set(this.selectedTranslations[key], lang.locale, true);
          }
        });
      });
    },

    deselectAll() {
      Object.keys(this.selectedTranslations).forEach((key) => {
        Object.keys(this.selectedTranslations[key]).forEach((locale) => {
          this.selectedTranslations[key][locale] = false;
        });
      });
      this.$forceUpdate();
    },

    selectOnlyMissing() {
      // First deselect all
      this.deselectAll();

      // Then select only translations for keys that have incomplete translations
      Object.keys(this.proposedTranslations).forEach((key) => {
        const allTranslationsForKey = this.getTranslationsForKey(key);
        let hasIncompleteTranslations = false;

        // Check if any non-Polish language is missing or empty
        this.availableLanguages.forEach((lang) => {
          if (lang.lang !== 'pl') {
            const existingTranslation = allTranslationsForKey[lang.locale];
            if (!existingTranslation || existingTranslation.trim() === '') {
              hasIncompleteTranslations = true;
            }
          }
        });

        // If this key has incomplete translations, select only the missing ones
        if (hasIncompleteTranslations) {
          if (!this.selectedTranslations[key]) {
            this.$set(this.selectedTranslations, key, {});
          }

          this.availableLanguages.forEach((lang) => {
            if (lang.lang !== 'pl' && this.proposedTranslations[key][lang.locale]) {
              const existingTranslation = allTranslationsForKey[lang.locale];
              // Select only if current translation is missing or empty
              const shouldSelect = !existingTranslation || existingTranslation.trim() === '';
              this.$set(this.selectedTranslations[key], lang.locale, shouldSelect);
            }
          });
        }
      });

      this.$forceUpdate();
    },

    getLocaleCode(languageCode) {
      const lang = this.availableLanguages.find((l) => l.lang === languageCode);
      return lang ? lang.locale : `${languageCode}-${languageCode.toUpperCase()}`;
    },

    getCurrentTranslation(key, languageCode) {
      const locale = this.getLocaleCode(languageCode);
      const allTranslations = this.getTranslationsForKey(key);
      return allTranslations[locale] || '';
    },

    getOriginalPolishText(key) {
      const allTranslations = this.getTranslationsForKey(key);
      return allTranslations['pl-PL'] || '';
    },

    acceptSelected() {
      const acceptedTranslations = {};

      Object.entries(this.selectedTranslations).forEach(([key, languages]) => {
        Object.entries(languages).forEach(([locale, isSelected]) => {
          if (isSelected && this.proposedTranslations[key]?.[locale]) {
            if (!acceptedTranslations[key]) {
              acceptedTranslations[key] = {};
            }
            acceptedTranslations[key][locale] = this.proposedTranslations[key][locale];
          }
        });
      });

      if (this.isSingleKey) {
        this.$emit('apply-to-fields', acceptedTranslations);
      } else {
        this.$emit('accept', acceptedTranslations);
      }
    },

    handleOverlayClick() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss" scoped>
// Custom modal styles
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.custom-modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 90vw;
  max-height: 90vh;
  width: 1200px;
  display: flex;
  flex-direction: column;
}

.custom-modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .modal-title {
    margin: 0;
    color: #495057;
  }

  .btn-close {
    background: none;
    border: none;
    padding: 0.25rem;
    color: #6c757d;
    cursor: pointer;

    &:hover {
      color: #495057;
    }
  }
}

.custom-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.translation-preview-modal {
  .translation-key code {
    font-size: 0.9em;
    word-break: break-all;
  }

  .translation-text {
    font-size: 0.9em;
    line-height: 1.4;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    border-left: 3px solid #007bff;
  }

  .language-translation-preview {
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  .language-label {
    font-size: 0.9em;
    color: #495057;
  }

  .current-translation {
    font-size: 0.85em;
    padding: 0.25rem 0.5rem;
    background-color: #f8f9fa;
    border-radius: 3px;
    min-height: 1.5em;
  }

  .proposed-translation {
    font-size: 0.9em;
    padding: 0.5rem;
    background-color: #d4edda;
    border-radius: 3px;
    border-left: 3px solid #28a745;
  }

  .translation-comparison {
    font-size: 0.9em;
  }

  .translations-preview-list {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    background-color: #fafbfc;
  }

  .translation-preview-item {
    background-color: #ffffff;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // Compact layout styles
  .translation-key-compact {
    font-size: 0.8em;
    font-weight: 500;
  }

  .language-translation-compact {
    min-height: 60px;

    &:hover {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }

  .language-label-compact {
    font-size: 0.75em;
    font-weight: 600;
    color: #495057;
  }

  .badge-compact {
    font-size: 0.7em;
    padding: 0.2em 0.4em;
  }

  .proposed-translation-compact {
    font-size: 0.8em;
    line-height: 1.3;
    color: #495057;
    margin-bottom: 0.25rem;
  }

  .current-compact {
    font-size: 0.7em;
    line-height: 1.2;
    max-height: 1.4em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0.7;
  }

  // Clickable card styles
  .key-header-clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
      border-radius: 4px;
      padding: 0.25rem;
      margin: -0.25rem;
    }
  }

  .clickable-card {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transform: translateY(-1px);
    }

    &.border-success {
      &:hover {
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
      }
    }
  }
}
</style>
